import { useEffect, useRef, useState, useMemo } from "react";
import QueryModal from "views/components/QueryModal";
import QueryModalInput from "views/components/QueryModal/QueryModalInput";
import QueryModalTitle from "views/components/QueryModal/QueryModalTitle";
import SubmitQueryButton from "views/components/QueryModal/SubmitQueryButton";
import CaptionIcon from "assets/icons/CaptionIcon";
import styles from "./style.module.scss";
import {
  createAndUpdateCaptionFilter,
  createAndUpdateSearchTextFilter,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";
import useVqlParams from "hooks/useVqlParams";
import { VqlQuery } from "types";
import { Filters } from "types";
import { useDispatch } from "react-redux";
import {
  setSelectedQueryModal,
  updateSelectedQueryModal,
} from "redux/SingleDataset/actions";
import { useTextQueryModal } from "hooks/UseQueryModal/useTextQueryModal";

interface CaptionQueryModalProps {
  editQueryId?: string;
  filterType?: CaptionFilterType;
}

type CaptionFilterType = Filters.CAPTION | Filters.SEMANTIC;
type OperationType = "semantic" | "fts";
export default function CaptionQueryModal({
  editQueryId,
  filterType = Filters.CAPTION,
}: CaptionQueryModalProps) {
  const { vqlQueries, findVqlQuery, addVqlQuery, updateVqlQuery } =
    useVqlParams();

  const operationByFilterType: Record<CaptionFilterType, OperationType> = {
    [Filters.SEMANTIC]: "semantic",
    [Filters.CAPTION]: "fts",
  };

  const operation = operationByFilterType[filterType];

  const oldQuery = editQueryId ? findVqlQuery(editQueryId, "text") : null;

  const existingQuery = useMemo(() => {
    return (
      vqlQueries.find(
        (query: VqlQuery) =>
          isVqlQuery(query, "text") &&
          query.text.op === operation &&
          query.id !== editQueryId,
      ) || null
    );
  }, [vqlQueries, operation, editQueryId]);

  const initialValue =
    oldQuery?.text?.value ||
    (existingQuery && isVqlQuery(existingQuery, "text")
      ? existingQuery.text?.value
      : "") ||
    "";
  const previousValue = oldQuery?.text?.value || "";
  const queryId = oldQuery?.id;

  const [searchText, setSearchText] = useState(initialValue);
  const modalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (editQueryId && queryId) {
      dispatch(updateSelectedQueryModal({ type: filterType, id: queryId }));
    }
  }, [editQueryId, filterType, queryId, dispatch]);

  useEffect(() => {
    setSearchText(initialValue);
  }, [editQueryId, initialValue]);

  const { isSubmitDisabled } = useTextQueryModal({
    inputValue: searchText,
    previousValue: editQueryId ? previousValue : initialValue,
    editQueryId,
    existingQueryId: existingQuery?.id,
  });

  const handleSubmission = () => {
    if (isSubmitDisabled) {
      return;
    }

    const helperByOperation: Record<
      OperationType,
      (
        text: string,
        id?: string,
        negation?: boolean,
        existingQuery?: VqlQuery,
      ) => VqlQuery
    > = {
      semantic: createAndUpdateSearchTextFilter,
      fts: createAndUpdateCaptionFilter,
    };

    const vqlQuery = helperByOperation[operation](
      searchText,
      editQueryId || existingQuery?.id,
      undefined,
      oldQuery || existingQuery || undefined,
    );
    editQueryId || existingQuery
      ? updateVqlQuery(vqlQuery)
      : addVqlQuery(vqlQuery);
    dispatch(setSelectedQueryModal(null));
  };

  const handleInputChange = (value: string) => {
    setSearchText(value);
  };

  return (
    <QueryModal modalRef={modalRef}>
      <QueryModalTitle icon={<CaptionIcon type="IMAGE" />}>
        {filterType === Filters.SEMANTIC ? "Semantic Search" : "Caption"}
      </QueryModalTitle>
      <div className={styles.filterContent}>
        <QueryModalInput
          searchText={searchText}
          onInputChange={handleInputChange}
        />

        <SubmitQueryButton
          isDisabled={isSubmitDisabled}
          onSubmit={handleSubmission}
        />
      </div>
    </QueryModal>
  );
}
